import Mark from 'mark.js';

export class TextHighlightService {
  static mark(querySelector: string, textToHighlight: string) {
    return new Promise<number>((resolve, reject) => {
      const markContainer = document.querySelector(querySelector) as HTMLElement;
      if (!markContainer) {
        return reject('Mark container not found');
      }

      const instance = new Mark(markContainer);

      instance.unmark({
        done: () => {
          const sourceText = markContainer.innerText;
          const highlightWords = this.extractWords(textToHighlight);

          if (!highlightWords.length || !sourceText) {
            // console.info('No words to highlight or no source text');
            return resolve(0);
          }

          // --- Attempt 1: Fuzzy LCS-based range matching ---
          // console.info('Trying fuzzy LCS matching first.');
          const sourceWords = this.extractWords(sourceText);
          const lcsWords = this.findLCS(highlightWords, sourceWords);

          const minMatchLength = Math.max(5, Math.floor(highlightWords.length * 0.9));
          let fuzzyMatchFound = false;

          if (lcsWords.length >= minMatchLength) {
            const bestRange = this.findBestMatchRange(
              sourceText,
              highlightWords,
              lcsWords,
              minMatchLength
            );

            if (bestRange) {
              const fuzzyMatchText = sourceText
                .substring(bestRange.start, bestRange.start + bestRange.length)
                .replace(/\s+/g, ' ')
                .trim();

              instance.mark(fuzzyMatchText, {
                className: 'marked-text-highlight',
                separateWordSearch: false,
                accuracy: {
                  value: 'complementary',
                  limiters: [
                    ',',
                    '.',
                    '(',
                    ')',
                    '[',
                    ']',
                    '!',
                    '?',
                    '/',
                    '\\',
                    '+',
                    '-',
                    '_',
                    ';',
                    ':',
                    ' ',
                    '\n',
                  ],
                },
                caseSensitive: false,
                done: (marksTotal: number) => {
                  // console.info(`Fuzzy match highlighting found ${marksTotal} matches`);
                  if (marksTotal > 0) {
                    fuzzyMatchFound = true;
                    resolve(marksTotal);
                  } else {
                    // Fall back to precise matching if fuzzy found no matches
                    // console.warn('Fuzzy mark() found no matches, trying precise matching');
                  }
                },
              });
            } else {
              // console.warn('Could not find a suitable fuzzy match range.');
            }
          } else {
            // console.warn('Initial LCS too short:', lcsWords.length, 'vs min:', minMatchLength);
          }

          // If fuzzy match worked, we're done
          if (fuzzyMatchFound) {
            return;
          }

          // --- Fallback: Precise 5-word anchor matching ---
          const MIN_WORDS_FOR_ANCHOR = 10;
          if (highlightWords.length >= MIN_WORDS_FOR_ANCHOR) {
            // console.info('Falling back to precise anchor matching.');
            const first5 = highlightWords.slice(0, 5);
            const last5 = highlightWords.slice(-5);

            // Allow whitespace OR non-word characters between words (more robust)
            const flexibleJoiner = '(?:\\s|\\W)+';
            const startPattern = first5.map(this.escapeRegex).join(flexibleJoiner);
            const endPattern = last5.map(this.escapeRegex).join(flexibleJoiner);

            try {
              const startRegex = new RegExp(startPattern, 'i'); // Case-insensitive
              const endRegex = new RegExp(endPattern, 'i'); // Case-insensitive

              const startMatch = startRegex.exec(sourceText);
              const endMatch = endRegex.exec(sourceText);

              if (startMatch && endMatch && startMatch.index < endMatch.index) {
                const start = startMatch.index;
                const end = endMatch.index + endMatch[0].length;
                let fullMatchedText = sourceText.substring(start, end);

                // Clean up the text - normalize spaces and line breaks
                fullMatchedText = fullMatchedText
                  .replace(/\s+/g, ' ') // Replace multiple spaces/newlines with single space
                  .trim();

                instance.mark(fullMatchedText, {
                  className: 'marked-text-highlight',
                  separateWordSearch: false,
                  accuracy: {
                    value: 'complementary',
                    limiters: [
                      ',',
                      '.',
                      '(',
                      ')',
                      '[',
                      ']',
                      '!',
                      '?',
                      '/',
                      '\\',
                      '+',
                      '-',
                      '_',
                      ';',
                      ':',
                      ' ',
                      '\n',
                    ],
                  },
                  caseSensitive: false,
                  done: (marksTotal: number) => {
                    console.info(`Precise anchor highlighting found ${marksTotal} matches`);
                    return resolve(marksTotal);
                  },
                  noMatch: () => {
                    console.warn('Precise mark() found no matches for text:', fullMatchedText);
                    // Last attempt with less strict matching
                    instance.mark(fullMatchedText, {
                      className: 'marked-text-highlight',
                      separateWordSearch: false,
                      accuracy: 'partially',
                      done: (fallbackMarksTotal: number) => {
                        console.info(`Fallback highlighting found ${fallbackMarksTotal} matches`);
                        resolve(fallbackMarksTotal);
                      },
                    });
                  },
                });
                return;
              }
            } catch (e) {
              console.error('Error during precise anchor regex:', e);
            }
          }

          // If we get here, no match was found
          console.warn('No matches found with any method');
          resolve(0);
        },
        noMatch: term => {
          console.warn('Unmark failed or found nothing to unmark for:', term);
        },
      });
    });
  }

  private static escapeRegex(str: string): string {
    return str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\\\$&');
  }

  private static extractWords(text: string): string[] {
    if (!text) return [];
    return text.match(/[a-zA-Z0-9]+/g) || [];
  }

  private static findLCS(arr1: string[], arr2: string[]): string[] {
    const m = arr1.length;
    const n = arr2.length;
    const dp: number[][] = Array(m + 1)
      .fill(0)
      .map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (arr1[i - 1].toLowerCase() === arr2[j - 1].toLowerCase()) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    const lcs: string[] = [];
    let i = m;
    let j = n;
    while (i > 0 && j > 0) {
      if (arr1[i - 1].toLowerCase() === arr2[j - 1].toLowerCase()) {
        lcs.push(arr1[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }
    return lcs.reverse();
  }

  private static findBestMatchRange(
    sourceText: string,
    highlightWords: string[],
    lcsWords: string[],
    minMatchLength: number
  ): { start: number; length: number } | null {
    if (!lcsWords.length) return null;

    const firstLCSWord = lcsWords[0];
    const lastLCSWord = lcsWords[lcsWords.length - 1];

    const firstWordRegex = new RegExp('\\b' + this.escapeRegex(firstLCSWord) + '\\b', 'gi');
    const lastWordRegex = new RegExp('\\b' + this.escapeRegex(lastLCSWord) + '\\b', 'gi');
    let firstWordMatch;
    const firstWordIndices: number[] = [];
    while ((firstWordMatch = firstWordRegex.exec(sourceText)) !== null) {
      firstWordIndices.push(firstWordMatch.index);
    }

    let lastWordMatch;
    const lastWordIndices: { index: number; length: number }[] = [];
    while ((lastWordMatch = lastWordRegex.exec(sourceText)) !== null) {
      lastWordIndices.push({ index: lastWordMatch.index, length: lastWordMatch[0].length });
    }

    if (!firstWordIndices.length || !lastWordIndices.length) {
      console.warn('Could not find anchor words:', firstLCSWord, lastLCSWord);
      return null;
    }

    let bestRange: { start: number; length: number } | null = null;
    let bestScore = -1;

    // Maximum reasonable length is 2x the length of the highlight text
    const maxRangeLength = highlightWords.join(' ').length * 2;

    for (const startIndex of firstWordIndices) {
      for (const endInfo of lastWordIndices) {
        const endIndex = endInfo.index + endInfo.length;
        if (endIndex > startIndex) {
          const currentLength = endIndex - startIndex;

          // Skip if range is too long
          if (currentLength > maxRangeLength) {
            continue;
          }

          const sub = sourceText.substring(startIndex, endIndex);
          const subWords = this.extractWords(sub);

          if (subWords.length === 0) continue;

          const currentLcs = this.findLCS(highlightWords, subWords);

          if (currentLcs.length < minMatchLength) {
            continue;
          }

          // Score based on:
          // 1. Length of LCS (more matches is better)
          // 2. Density of matches (matches per character is better)
          // 3. Closer to original text length (penalize being too long or too short)
          const lcsScore = currentLcs.length;
          const densityScore = lcsScore / currentLength;
          const originalLength = highlightWords.join(' ').length;
          const lengthPenalty = Math.abs(currentLength - originalLength) / originalLength;

          const score = (lcsScore * densityScore) / (1 + lengthPenalty);

          if (score > bestScore) {
            bestScore = score;
            bestRange = { start: startIndex, length: currentLength };
          }
        }
      }
    }

    return bestRange;
  }

  public static navigateToMarked(
    querySelector: string,
    marksTotal = 0,
    containerQuerySelector = '.source-content-container'
  ) {
    setTimeout(() => {
      const markedElements = document.querySelectorAll(querySelector);
      if (marksTotal > 0 && markedElements.length > 0) {
        const firstMarked = markedElements[0];
        firstMarked.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else {
        const container = document.querySelector(containerQuerySelector);
        if (container) {
          container.scrollTop = 10;
        }
      }
    }, 100);
  }
}
